import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { useRouter } from 'next/router';
import { toast } from 'sonner';

export const useSeparateGroup = () => {
  const utils = api.useUtils();
  const { track } = useAnalytics();
  const router = useRouter();

  return api.sidebar.separate.useMutation({
    onMutate: async (groupId: string) => {
      // Snapshot the previous workspace data
      const prevData = utils.workspace.all.getData();

      // Check if the current route is the group being separated
      const currentId = router.query.id as string;
      if (currentId === groupId && prevData) {
        const groupToSeparate = prevData.entities.find(
          (entity) => entity.id === groupId,
        );

        if (groupToSeparate && groupToSeparate.type === 'GROUP') {
          // Navigate to the parent group or home immediately
          if (groupToSeparate.groupId) {
            // Navigate to parent group
            router.push(`/b/${groupToSeparate.groupId}`);
          } else {
            // Navigate to home if no parent
            router.push('/new');
          }
        }
      }

      // Calculate information for toast
      let separateInfo = null;
      if (prevData) {
        const groupToSeparate = prevData.entities.find(
          (entity) => entity.id === groupId,
        );

        if (groupToSeparate && groupToSeparate.type === 'GROUP') {
          // Count children being moved
          const childrenBeingMoved = prevData.entities.filter(
            (entity) => entity.groupId === groupId,
          );

          // Get destination folder name
          let destinationFolderName = 'Library';
          if (groupToSeparate.groupId) {
            const parentGroup = prevData.entities.find(
              (entity) => entity.id === groupToSeparate.groupId,
            );
            if (parentGroup?.name) {
              destinationFolderName = parentGroup.name;
            }
          }

          separateInfo = {
            count: childrenBeingMoved.length,
            destinationFolderName,
          };
        }
      }

      // Track the analytics event
      track('group_separate');

      // Show toast notification
      if (separateInfo) {
        const { count, destinationFolderName } = separateInfo;
        const fileText = count === 1 ? 'file' : 'files';
        toast.success(`${count} ${fileText} moved to ${destinationFolderName}`);
      }

      // Optimistically update the workspace
      utils.workspace.all.setData(undefined, (old) => {
        if (!old) return old;

        // Find the group being separated
        const groupToSeparate = old.entities.find(
          (entity) => entity.id === groupId,
        );
        if (!groupToSeparate || groupToSeparate.type !== 'GROUP') return old;

        // Find all children of this group and update their groupId to move them to the parent level
        const updatedEntities = old.entities
          .map((entity) => {
            // If this entity is a child of the group being separated,
            // move it to the group's parent level
            if (entity.groupId === groupId) {
              return {
                ...entity,
                groupId: groupToSeparate.groupId, // Move to parent level (or null if group was at root)
              };
            }
            return entity;
          })
          // Remove the group itself from the workspace
          .filter((entity) => entity.id !== groupId);

        return {
          ...old,
          entities: updatedEntities,
        };
      });

      // Return context for rollback
      return { prevData };
    },
    onError: (_err, _variables, context) => {
      // Rollback to previous state if mutation fails
      if (context?.prevData) {
        utils.workspace.all.setData(undefined, context.prevData);
      }
    },
    onSuccess: (_) => {
      // Track the analytics event
      track('group_separate');
    },
  });
};
